/* eslint-disable no-param-reassign */
import { useCallback, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { FinancialStatementPeriod } from 'common/types/financials';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { useFinancials } from 'context/FinancialsContext';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import FilesService from 'services/documents';
import logger from 'utilities/logger';
import { AttributeMappingType, NEGATIVE_PREFIX } from './types';
import {
  ApiCreateDocumentGenerationJob,
  CompanyService,
  DocumentReferencesView,
} from '../../../api';
import { useStore } from '../../../common/store';
import { UseStoreValues } from '../../../common/types/store';
import { HandlePushingAttributeMappingsPayload } from '../../../components/Drawer/attribute-mapped-widget-drawer/attribute-mapped-widget-drawer.types';
import { AttributeMappedWidgetDrawer } from '../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawer';
import { XLSX_VIEWER_STRINGS } from '../../../components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import WidgetDrawerProps from '../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { EventType, useEventBus, useEventSubscription } from '../../../context';
import { useLayoutContext } from '../../../context/LayoutContext';
import { CellValue } from '../../../models/interfaces/spreadsheet-types/core-types';
import {
  AttributeType,
  useAcceptAllAIMappings,
  useRetrieveAttributeMapper,
} from '../../../services/hooks/attributeMapper';
import { POLL_TYPE, useAppLoadAsyncOperations } from '../../../services/hooks/useAppLoadAsyncActions';
import { FinancialsReferenceGenerator } from '../../../services/reference-generator';
import { ClearAttributeMappingsPayload } from '../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import SheetAttributeManager from '../../../services/sheet-attribute-manager/SheetAttributeManager';
import { useCellMappingStore } from '../../../store';
import { useAttributeMapperStore } from '../../../store/useAttributeMapperStore';
import { Logger } from '../../../utilities';
import { JSONGenerationJobParsingMode } from '../../DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { rowConfig as balanceSheetRowConfig } from '../balance-sheet/data';
import { BalanceSheetCell } from '../balance-sheet/types';
import { rowConfig as incomeStatementRowConfig } from '../income-statement/data';
import { FinancialsCellWithReferences } from '../types/cell-with-references';

export interface FinancialsWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
  incomeStatementSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
  balanceSheetSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
}

const FinancialsWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
  incomeStatementSetProgrammaticUpdate,
  balanceSheetSetProgrammaticUpdate,
}: FinancialsWidgetDrawerProps) => {
  const filesService = useMemo(() => new FilesService(), []);
  const { currentMeasurementDate } = useLayoutContext();
  const [retrieveAttributeMapper] = useRetrieveAttributeMapper();

  const { ...items } = useFinancials();
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager, clearAttributeMapperManager, setAttributeMapperManager } =
    useAttributeMapperStore();
  const [acceptAllAIMappings] = useAcceptAllAIMappings();
  const { manuallyExecutePoll } = useAppLoadAsyncOperations(false);
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const { publish } = useEventBus();
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [documentGenJobMetadata, setDocumentGenJobMetadata] = useState<{
    document_reference_id: number;
    measurement_date_id: number;
    file_id: number;
  } | null>(null);
  const queryClient = useQueryClient();

  // opportunity to memoize this
  const incomeStatementIndex = (alias: string) =>
    incomeStatementRowConfig(false).findIndex(item => item.alias === alias);

  const balanceSheetIndex = (alias: string) => balanceSheetRowConfig(false).findIndex(item => item.alias === alias);

  const clearCellReferences = (docRefPayload?: ClearAttributeMappingsPayload) => {
    const cellList = Object.values(workbook.incomeStatement.cells as FinancialsCellWithReferences[])
      .concat(Object.values(workbook.balanceSheet.cells as FinancialsCellWithReferences[]))
      .filter(item => {
        const hasMappedRefs = item.mappingReferences?.length;
        const hasColumnarRefs = item.columnarReferences?.length;
        const hasColRefsMetadata = item.columnarReferencesMetadata?.length;

        return hasMappedRefs || hasColumnarRefs || hasColRefsMetadata;
      });

    const cellUpdates: { cell: FinancialsCellWithReferences; value: CellValue }[] = [];
    cellList.forEach(cell => {
      // If no docRefPayload, clear all references
      if (!docRefPayload) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = '';
        cell.valueViewer = null;

        cellUpdates.push({
          cell,
          value: cell.value,
        });
        return;
      }

      // Otherwise, filter references for specific document/sheet
      const { documentReferenceId, sheet } = docRefPayload;
      let hasChanges = false;

      // Filter mappingReferences
      if (cell.mappingReferences && cell.mappingReferences.length > 0) {
        const originalLength = cell.mappingReferences.length;
        cell.mappingReferences = cell.mappingReferences.filter(ref => {
          const matchesDoc = ref.document_reference === documentReferenceId;
          const matchesSheet = !sheet || ref.sheet_name === sheet;
          return !(matchesDoc && matchesSheet);
        });
        hasChanges = hasChanges || originalLength !== cell.mappingReferences.length;
      }

      // If all mappingReferences are removed, clear the cell styling
      if (hasChanges && (!cell.mappingReferences || cell.mappingReferences.length === 0)) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = '';
        cell.valueViewer = null;
      }

      if (hasChanges) {
        cellUpdates.push({
          cell,
          value: cell.value,
        });
      }
    });

    cellUpdates.forEach(change => {
      onTableChange(change.cell, change.value as number);
    });

    // Recalculate title cells for affected columns
    if (docRefPayload && cellUpdates.length > 0) {
      const referenceGeneratorService = new FinancialsReferenceGenerator();
      const affectedColumns = new Set<string>();

      cellUpdates.forEach(({ cell }) => {
        const { columnLegend } = cell as any;
        if (columnLegend) {
          affectedColumns.add(columnLegend);
        }
      });

      affectedColumns.forEach(columnLegend => {
        // Recalculate for income statement
        const incomeTitleCell = workbook.incomeStatement.cells[`${columnLegend}1`] as FinancialsCellWithReferences;
        if (incomeTitleCell) {
          const columnMappingReferences: any[] = [];
          Object.keys(workbook.incomeStatement.cells).forEach(key => {
            if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
              const cell = workbook.incomeStatement.cells[key] as FinancialsCellWithReferences;
              if (cell.mappingReferences && cell.mappingReferences.length > 0) {
                columnMappingReferences.push(...cell.mappingReferences);
              }
            }
          });
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          incomeTitleCell.columnarReferences = docRefsData.documentIds;
          incomeTitleCell.columnarReferencesMetadata = docRefsData.metadata as any;
          incomeTitleCell.dispatchEvent();
        }

        // Recalculate for balance sheet
        const balanceTitleCell = workbook.balanceSheet.cells[`${columnLegend}1`] as FinancialsCellWithReferences;
        if (balanceTitleCell) {
          const columnMappingReferences: any[] = [];
          Object.keys(workbook.balanceSheet.cells).forEach(key => {
            if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
              const cell = workbook.balanceSheet.cells[key] as FinancialsCellWithReferences;
              if (cell.mappingReferences && cell.mappingReferences.length > 0) {
                columnMappingReferences.push(...cell.mappingReferences);
              }
            }
          });
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          balanceTitleCell.columnarReferences = docRefsData.documentIds;
          balanceTitleCell.columnarReferencesMetadata = docRefsData.metadata as any;
          balanceTitleCell.dispatchEvent();
        }
      });
    }
  };

  const handleAcceptClassification = useCallback(
    async (docRefIdsWithMappings: number[]) => {
      try {
        const fileRefsToUpdate = docRefIdsWithMappings.map((docRefId: number) =>
          filesService.approveClassification(String(docRefId))
        );
        await Promise.all(fileRefsToUpdate);
        queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });
      } catch (error) {
        logger.error('Error handling accept classification', error);
        enqueueSnackbar('Failed to accept classification', { variant: 'error' });
      }
    },
    [filesService, queryClient]
  );

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: FinancialsCellWithReferences }]) => {
      const refs = newCell.cellInfo.mappingReferences;
      if (refs && refs.length > 0) {
        const first = refs[0];
        publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
          documentReferenceId: first.document_reference,
          sheetName: first.sheet_name,
        });
      }
    }
  );

  useEventSubscription(EventType.FIN_MAP_ATT_MAPPER_UPDATED_VIA_GEN_JOB_COMPLETED, async (jobId: string) => {
    if (!attributeMapperManager?.sheetAttributeMapping.id) {
      return;
    }
    const retrievedMapper = await retrieveAttributeMapper(
      attributeMapperManager?.sheetAttributeMapping.id,
      AttributeType.FINANCIALS
    );

    const mapping = new SheetAttributeManager(retrievedMapper);

    setAttributeMapperManager(mapping);
  });

  const pushAttributeMappingsToFinancials = async (payload?: HandlePushingAttributeMappingsPayload) => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }

    // Set flag to indicate programmatic updates are happening
    incomeStatementSetProgrammaticUpdate?.(true);
    balanceSheetSetProgrammaticUpdate?.(true);

    try {
      const mappingService = new FinancialsReferenceGenerator();
      const mapping = getCellMapping();
      const evaluatedAttributeData = mapper.calculateValues(mapping);

      // Get the sheet attribute mapping which contains the reference data
      const { sheetAttributeMapping } = mapper;

      // Generate mapping references from the sheet attribute data (not evaluated data)
      const generatedReferences = mappingService.generateMappingReferences(sheetAttributeMapping.data);

      const incomeChangeSet: CellChange[] = [];
      const balanceChangeSet: CellChange[] = [];

      clearCellReferences();

      Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
        Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
          const incomeStatementRowIndex = incomeStatementIndex(rowMetric);

          const findCell = (item: BalanceSheetCell) => {
            if (item.alias !== rowMetric) {
              return false;
            }
            if (columnarMetric === 'LTM') {
              return item.isLTM;
            }
            if (columnarMetric === 'NTM') {
              return item.isNTM;
            }
            // Ensure both are compared as strings to avoid type mismatch issues
            return String(item.year) === String(columnarMetric);
          };

          if (incomeStatementRowIndex >= 0) {
            const updatedCell = Object.values(workbook.incomeStatement.cells as BalanceSheetCell[]).find(findCell) as
              | FinancialsCellWithReferences
              | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // Add mapping reference metadata to cell
              const titleCell = workbook.incomeStatement.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                updatedCell.mappingReferences = referenceMetadata;
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                incomeChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                updatedCell.mappingReferences = [];
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              incomeChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
            return;
          }

          const balanceSheetRowIndex = balanceSheetIndex(rowMetric);
          if (balanceSheetRowIndex >= 0) {
            const updatedCell = Object.values(workbook.balanceSheet.cells as BalanceSheetCell[]).find(findCell) as
              | FinancialsCellWithReferences
              | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // Add mapping reference metadata to cell
              const titleCell = workbook.balanceSheet.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                updatedCell.mappingReferences = referenceMetadata;
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                balanceChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                updatedCell.mappingReferences = [];
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              balanceChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
          }
        });
      });

      const fullChanges = incomeChangeSet.concat(balanceChangeSet);
      fullChanges.forEach(change => {
        onTableChange(change.cell, change.value);
      });

      // After successful push, auto-accept all AI-generated mappings
      // This acknowledges the analyst's action of pushing mappings
      const mappingId = sheetAttributeMapping.id;
      // if (mappingId && attributeMapperManager?.hasUnacceptedAIMappings()) {
      const refIdsOnMapping = Array.from(
        new Set(sheetAttributeMapping.sheetIndicatorMapping.map(i => i.documentReferenceId) ?? [])
      );
      await handleAcceptClassification(refIdsOnMapping);

      if (mappingId && attributeMapperManager?.hasUnacceptedAIMappings()) {
        try {
          const updatedMapping = await acceptAllAIMappings(mappingId);
          // Update the store with accepted mappings
          setAttributeMapperManager(new SheetAttributeManager(updatedMapping));
          logger.info('FinancialsWidgetDrawer', `Auto-accepted all AI mappings for mapping ${mappingId}`);
        } catch (error) {
          logger.error('FinancialsWidgetDrawer - Error auto-accepting AI mappings', error);
          // Don't block the push on auto-accept failure
        }
      }

      if (payload?.showNotification) {
        enqueueSnackbar('Mapping pushed to financials', { variant: 'success' });
      }
    } finally {
      // Reset flag after all changes are complete
      incomeStatementSetProgrammaticUpdate?.(false);
      balanceSheetSetProgrammaticUpdate?.(false);
    }
  };

  const handleClearMappings = (docRefPayload?: ClearAttributeMappingsPayload) => {
    clearCellReferences(docRefPayload);
    clearAttributeMapperManager(docRefPayload);
    pushAttributeMappingsToFinancials({ showNotification: false });
  };

  const handleReprocessClick = useCallback(
    (documentClicked: DocumentReferencesView) => {
      setDocumentGenJobMetadata({
        document_reference_id: documentClicked.document_reference_id,
        measurement_date_id: currentMeasurementDate?.id,
        file_id: documentClicked.file.id,
      });
      setReprocessDialogOpen(true);
    },
    [currentMeasurementDate?.id]
  );

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    if (!documentGenJobMetadata) {
      enqueueSnackbar('No document processing job metadata found', { variant: 'error' });
      return;
    }

    try {
      if (!companyInfo?.id) {
        enqueueSnackbar('Company information not available', { variant: 'error' });
        return;
      }

      // Create a new generation job for the same document
      await CompanyService.apiCompanyDocumentGenerationJobsNewCreate(companyInfo.id.toString(), {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.FINANCIAL_MAPPING,
        reference_files: [documentGenJobMetadata?.file_id],
        desired_termination_state: 'COMPLETE',
        parser_overrides: [
          {
            file_type: 'json',
            json_parsing_mode: JSONGenerationJobParsingMode.XLSX_JSON_MINIFIER,
          },
        ],
        metadata: {
          document_reference_id: documentGenJobMetadata?.document_reference_id,
          measurement_date_id: documentGenJobMetadata?.measurement_date_id,
          file_id: documentGenJobMetadata?.file_id,
        },
      });

      // Start the app-level polling loop to track this job across navigation.
      manuallyExecutePoll(POLL_TYPE.GENERATION_JOBS);

      enqueueSnackbar('Reprocessing started. New suggestions will appear when complete.', { variant: 'info' });
    } catch (error: any) {
      Logger.error('FinancialsWidgetDrawer - Error reprocessing document', error);
      const errorMessage = error?.body?.detail || error?.message || 'Failed to reprocess document';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    }
  };

  const handleReprocessCancel = () => {
    setDocumentGenJobMetadata(null);
    setReprocessDialogOpen(false);
  };

  const { periods } = items;

  const filterRows = (row: any) => !row.readOnly;
  const mapRowsToOptions = (row: any) => ({ label: row.value, value: row.alias });
  const mapNegRowsToOptions = (row: any) => ({ label: `-${row.value}`, value: `${NEGATIVE_PREFIX}${row.alias}` });

  const mapPeriodType = (period: FinancialStatementPeriod) => {
    if (period.period_type === undefined) {
      logger.error(`Period type must be defined. Period: ${JSON.stringify(period)}`, undefined);
      return {
        label: period.name || '',
        value: period.name || '',
      };
    }
    if (period.period_type < 3) {
      return {
        label: period.year || '',
        value: period.year || '',
      };
    }
    return {
      label: period.name || '',
      value: period.name || '',
    };
  };

  const attributeReferenceConfig = useMemo(() => {
    if (periods) {
      const columns = periods.map(mapPeriodType).filter((col): col is NonNullable<typeof col> => col !== undefined);
      const balanceSheetRows = balanceSheetRowConfig(false);
      const incomeStatementRows = incomeStatementRowConfig(false);

      // Map options and enrich with ordinate metadata for AI suggestion chips
      const enrichWithOrdinate = (option: any) => ({
        ...option,
        ordinate: attributeMapperManager?.getOrdinateMetadata(option.value as string),
      });

      const incomeStatementOptions = incomeStatementRows
        .filter(filterRows)
        .map(mapRowsToOptions)
        .map(enrichWithOrdinate);
      const balanceSheetOptions = balanceSheetRows.filter(filterRows).map(mapRowsToOptions).map(enrichWithOrdinate);

      const negIncomeStatementOptions = incomeStatementRows
        .filter(filterRows)
        .map(mapNegRowsToOptions)
        .map(enrichWithOrdinate);
      const negBalanceSheetOptions = balanceSheetRows
        .filter(filterRows)
        .map(mapNegRowsToOptions)
        .map(enrichWithOrdinate);
      const rows = [
        { label: 'Income Statement', value: null, isTitle: true },
        ...incomeStatementOptions,
        { label: 'Balance Sheet', value: null, isTitle: true },
        ...balanceSheetOptions,

        { label: '(-)Income Statement', value: null, isTitle: true },
        ...negIncomeStatementOptions,
        { label: '(-)Balance Sheet', value: null, isTitle: true },
        ...negBalanceSheetOptions,
      ];
      return {
        columns,
        rows,
        selectionMode: AttributeMappingType.GRID_INDICATORS,
      };
    }
    return { columns: [], rows: [], selectionMode: AttributeMappingType.GRID_INDICATORS };
  }, [periods, attributeMapperManager]);

  return (
    <>
      <AttributeMappedWidgetDrawer
        documentApplicationStorageKey={documentApplicationStorageKey}
        attributeMapper={attributeMapperManager}
        attributeReferenceConfig={attributeReferenceConfig}
        applicationContext={applicationContext}
        documentReferencesProps={documentReferencesProps}
        notesProps={notesProps}
        spreadsheetNotesProps={spreadsheetNotesProps}
        handlePushingAttributeMappings={pushAttributeMappingsToFinancials}
        handleClearMappings={handleClearMappings}
        onReprocessDocument={handleReprocessClick}
        mappingReprocessPrimaryLabel={XLSX_VIEWER_STRINGS.BUTTON.GENERATE_MAPPINGS}
        onCitationPushed={() => {}}
        showPushMappings
      />

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reprocess the document with the AI mapping system. Accepted and human-created mappings will be
            preserved. Only unaccepted AI suggestions will be replaced with new ones.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => handleReprocessConfirm()} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinancialsWidgetDrawer;
